/* PlannerCaseDetail — full-page case workspace, organised by tabs.
 *
 * Layout (matches the agreed mock):
 *   [Back link]                                        [Open in Lab Chat]
 *   ┌───────────────────────────────────────────────────────────────────┐
 *   │  AV  Patient name (h1)                          Clinic name       │
 *   │      ID: INV-PT-XXXXXX · Open patient profile                     │
 *   │  ─────────────────────────────────────────────────────────────    │
 *   │  STATUS · CASE CODE · CREATED ON · STAGE                          │
 *   └───────────────────────────────────────────────────────────────────┘
 *
 *   [Doctor's submission] [Author the plan] [My notes]
 *   <tab content>
 *
 * Patient phone is intentionally never rendered (PII redaction for COE).
 * "Open patient profile" deep-links to the planner queue filtered by
 * patient_code so the planner can see every case for that patient in one
 * place — until a dedicated patient profile exists.
 */
import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

import { Tabs, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
    Cube, Image as ImageIcon, PaperPlaneRight,
    PlayCircle, CheckCircle,
    ChatCircleDots, ArrowLeft, NotePencil, ClipboardText,
    PencilSimple, FloppyDisk, Hourglass, Lock, ClockClockwise, Wrench,
    ArrowsClockwise, HandPalm, CaretDown, Sparkle, FileText,
    Question, WarningCircle, Factory,
    // iter98.99m — Step-accent icons for SubmissionTab cards.
    Stethoscope, Camera, Tooth,
} from "@phosphor-icons/react";
import PlanEditorInline from "@/components/case/PlanEditorInline";
import WearScheduleSection from "@/components/case/WearScheduleSection";
import usePlanCompletion from "@/components/case/usePlanCompletion";
import PlanReadinessScorecard from "@/components/case/PlanReadinessScorecard";
import DemoFillPlanButton from "@/components/case/DemoFillPlanButton";
import CaseSubmittedDialog from "@/components/case/CaseSubmittedDialog";
// iter98.97 — PreSubmissionChecklist removed: each accordion in the
// editor above validates its own section and the submit button surfaces
// blockers directly. The checklist was duplicate cognitive load. The
// import is intentionally dropped.
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import AutosaveBadge from "@/components/case/AutosaveBadge";
import CaseChatThread from "@/components/case/CaseChatThread";
import CaseTimelineTab from "@/components/case/CaseTimelineTab";
import CaseNotesJournal from "@/components/case/CaseNotesJournal";
import CasePdfMenu from "@/components/case/CasePdfMenu";
import RevisionRequestPanel from "@/components/coe/RevisionRequestPanel";
import PlannerTierCards from "@/components/planner/PlannerTierCards";
import PlannerCaseBriefCard, { countBriefSections } from "@/components/coe/PlannerCaseBriefCard";
import PlannerPerToothCard from "@/components/coe/PlannerPerToothCard";
import PlannerSubmissionRecords from "@/components/coe/PlannerSubmissionRecords";
import PlannerSubmissionFlagAlert from "@/components/coe/PlannerSubmissionFlagAlert";
import ManufacturingSpecForm from "@/components/planner/ManufacturingSpecForm";
import UnlockRequestControl from "@/components/planner/UnlockRequestControl";
import PlannerCaseCommandHeader from "@/components/planner/PlannerCaseCommandHeader";
import PlannerWorkspaceDeck from "@/components/planner/PlannerWorkspaceDeck";
import WorkspaceSection from "@/components/planner/WorkspaceSection";
import {
    expandRevisionItems, groupBySection, latestRevision,
} from "@/lib/revisionSections";


const STATUS_PILLS = {
    submitted:                { label: "Awaiting routing",   cls: "bg-amber-100 text-amber-700" },
    assigned_to_planner:      { label: "Pending acceptance", cls: "bg-amber-100 text-amber-800" },
    refinement_requested:     { label: "Refinement",         cls: "bg-amber-100 text-amber-800" },
    in_planning:              { label: "In planning",        cls: "bg-violet-100 text-violet-700" },
    submitted_to_qa:          { label: "Sent to QA",         cls: "bg-blue-100 text-blue-700" },
    sent_for_approval:        { label: "With doctor",        cls: "bg-cyan-100 text-cyan-700" },
    ready_for_review:         { label: "With doctor",        cls: "bg-cyan-100 text-cyan-700" },
    info_requested:           { label: "ADR — hold",         cls: "bg-orange-100 text-orange-700" },
    revision_requested:       { label: "Plan revision",      cls: "bg-rose-100 text-rose-700" },
    approved:                 { label: "Approved",           cls: "bg-emerald-100 text-emerald-700" },
    /* iter98.99ch — MFG spec authoring window + admin-routing handoff. */
    approved_pending_mfg:           { label: "MFG Spec Pending",    cls: "bg-amber-100 text-amber-800" },
    awaiting_production_assignment: { label: "Awaiting Production", cls: "bg-amber-100 text-amber-700" },
    // Post-approval states — case has left the planner's hands but the workspace remains visible
    assigned_to_production:   { label: "In production queue", cls: "bg-indigo-100 text-indigo-700" },
    in_production:            { label: "Manufacturing",       cls: "bg-indigo-100 text-indigo-700" },
    shipped:                  { label: "Shipped",             cls: "bg-teal-100 text-teal-700" },
    in_treatment:             { label: "In treatment",        cls: "bg-emerald-100 text-emerald-700" },
    treatment_started:        { label: "In treatment",        cls: "bg-emerald-100 text-emerald-700" },
    completed:                { label: "Completed",           cls: "bg-slate-100 text-slate-700" },
    cancelled:                { label: "Cancelled",           cls: "bg-slate-100 text-slate-500" },
    // Doctor-planned workflow states (a planner may open these read-only for reference)
    plan_authoring:           { label: "Doctor authoring",    cls: "bg-amber-100 text-amber-700" },
    submitted_to_production:  { label: "Sent to lab",         cls: "bg-indigo-100 text-indigo-700" },
};

const SCAN_KINDS = ["stl", "scan_upper", "scan_lower", "scan_bite"];
const PHOTO_KINDS = [
    "intraoral", "intraoral_front", "intraoral_left", "intraoral_right",
    "intraoral_upper_occlusal", "intraoral_lower_occlusal",
    "extraoral_front", "extraoral_profile",
];
const XRAY_KINDS = ["xray_opg", "xray_lateral_ceph"];
const SETUP_KINDS = ["setup", "setup_plan"];


function fmtDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }).replace(/\s/g, "-");
}


export default function PlannerCaseDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [c, setC] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reviewNote, setReviewNote] = useState("");
    // iter98.fix-tier-ownership — planner picks ONLY the treatment tier
    // now. Manufacturing speed is locked-in by the doctor at Step 6 of
    // the wizard and shown read-only on the planner's PlanTab. The
    // `selectedPriorityId` state is kept for legacy callers but stays
    // empty for the new flow (we don't post a priority override to the
    // backend unless the user explicitly changed it).
    const [selectedTierId, setSelectedTierId] = useState("");
    const [selectedPriorityId, setSelectedPriorityId] = useState("");
    const [tiersList, setTiersList] = useState([]);
    const [priorityTiers, setPriorityTiers] = useState([]);
    /* iter98.99cg — Treatment tier auto-selection.
       Once the planner explicitly picks (or one is already saved on the
       case), we lock the choice and stop auto-overriding. This ref
       mirrors that intent — when true, the aligner-count-driven effect
       below becomes a no-op. */
    const tierManuallyPickedRef = useRef(false);
    const [tierAutoSelected, setTierAutoSelected] = useState(false);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    // Inline planner-to-doctor query — lives in the Submission tab's
    // Decision panel. Doubles as the ADR note when the planner clicks
    // "Request more info".
    const [queryNote, setQueryNote] = useState("");
    /* iter98.76 — universal premium notification dialog (mirrors the
     * doctor-submission dialog). Tracks which flow is showing and the
     * deferred navigation target so the planner sees the confirmation
     * BEFORE being routed back to their queue. */
    const [confirmDialog, setConfirmDialog] = useState({ open: false, flow: null });
    const fileInput = useRef(null);

    const load = () => {
        setLoading(true);
        api.get(`/cases/${id}`)
            .then((r) => {
                setC(r.data);
                // Pre-seed tier + priority pickers from the case's pricing
                // so the planner can confirm/override what's already there.
                const pr = r.data?.pricing || {};
                if (pr.tier_id) {
                    setSelectedTierId(pr.tier_id);
                    // A persisted tier means someone (planner or admin)
                    // already committed → lock auto-select.
                    tierManuallyPickedRef.current = true;
                    setTierAutoSelected(false);
                }
                if (pr.priority_tier_id) setSelectedPriorityId(pr.priority_tier_id);
            })
            .catch(() => {
                toast.error("Case not found");
                navigate("/coe");
            })
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    // iter98.fix-tier-ownership — fetch the active tier + priority tier
    // lists once so the planner can pick from the same list the doctor's
    // wizard renders for Self-Plan cases. CoE-only flow but cheap to
    // load for everyone.
    useEffect(() => {
        api.get("/tiers/active").then((r) => setTiersList(r.data || [])).catch(() => {});
        api.get("/priority-tiers/active").then((r) => setPriorityTiers(r.data || [])).catch(() => {});
    }, []);

    /* iter98.99cg — Auto-select treatment tier based on the planner's
       authored aligner count. The "total aligners" yardstick here is
       max(upper, lower) — same yardstick PlanEditorInline uses to drive
       the IPR allocator. We only auto-select when:
         · a real aligner count exists (>0)
         · the planner hasn't explicitly picked a tier yet
         · the count falls inside one of the active tiers' [min..max]
       If the count is outside every tier's range we leave the choice
       blank and the existing "no tier selected" copy remains.
       (Out-of-range warning is surfaced visually inside the tier
       picker block — see `tierMatchWarning` further below.) */
    useEffect(() => {
        if (tierManuallyPickedRef.current) return;
        if (!tiersList || tiersList.length === 0) return;
        if (!c?.plan) return;
        const up = Number(c.plan.aligner_count_upper || 0);
        const lo = Number(c.plan.aligner_count_lower || 0);
        const total = Math.max(up, lo);
        if (total <= 0) return;
        const match = tiersList.find((t) => {
            const tMin = Number(t.aligner_min || 0);
            const tMax = Number(t.aligner_count || 0);
            if (tMax <= 0) return false;
            return total >= tMin && total <= tMax;
        });
        if (match && match.id !== selectedTierId) {
            setSelectedTierId(match.id);
            setTierAutoSelected(true);
        } else if (match && !tierAutoSelected) {
            setTierAutoSelected(true);
        }
    }, [c?.plan?.aligner_count_upper, c?.plan?.aligner_count_lower, tiersList, selectedTierId, tierAutoSelected]); // eslint-disable-line react-hooks/exhaustive-deps

    /* Wrapped tier-setter — flips the lock so subsequent aligner-count
       changes never override the planner's explicit pick. */
    const handleManualTierSelect = useCallback((tier) => {
        tierManuallyPickedRef.current = true;
        setTierAutoSelected(false);
        setSelectedTierId(tier?.id || "");
    }, []);

    /* When the planner opens a case the doctor just resubmitted, ack it
       automatically so the queue badge clears for the next visit. The
       banner UI keeps `last_info_request_note` around for context until
       the next page-load (it's already part of the loaded `c` object). */
    useEffect(() => {
        if (c?.info_provided_at) {
            api.put(`/planner/cases/${c.id}/acknowledge-info-response`).catch(() => {
                // Non-fatal — the badge will simply stay until the next ack
                // attempt. No toast — this is a silent housekeeping call.
            });
        }
    }, [c?.id, c?.info_provided_at]);

    /* Preserve the bucket the planner came from so "Back to queue" returns
       them to the same filter view they were on. */
    const backLink = () => {
        const params = new URLSearchParams(location.search);
        const fromBucket = params.get("from");
        return fromBucket ? `/coe/planner/queue?bucket=${fromBucket}` : "/coe/planner/queue";
    };

    /* Tab driven by status — start on whichever tab is most actionable.
       Allows the planner to override their pick via URL (?tab=…). */
    const initialTab = useMemo(() => {
        const fromUrl = new URLSearchParams(location.search).get("tab");
        if (fromUrl && ["submission", "plan", "mfg", "chat", "notes", "timeline"].includes(fromUrl)) return fromUrl;
        // iter98.99ch — Land on MFG tab as soon as the doctor approves
        // so the planner sees the new actionable surface immediately.
        if (c?.status === "approved_pending_mfg") return "mfg";
        if (c?.status === "in_planning" || c?.status === "submitted_to_qa") return "plan";
        return "submission";
    }, [c?.status]); // eslint-disable-line react-hooks/exhaustive-deps
    const [tab, setTab] = useState("submission");
    useEffect(() => { setTab(initialTab); }, [initialTab]);

    /* ─── iter98.89 — Revision-Loop Inline Authoring ─────────────────
       The doctor's latest open revision_request is surfaced INLINE
       inside the Author the plan tab — each affected section gets a
       rose banner above its accordion showing the requested change
       and per-row Accept / Deny CTAs. Decisions live here so the
       Resubmit footer (also in this component) stays in sync with the
       banner rows. */
    const openRevision = useMemo(
        () => {
            const rev = latestRevision(c?.revision_requests || []);
            return rev && rev.status === "open" ? rev : null;
        },
        [c?.revision_requests],
    );
    const revisionRows = useMemo(
        () => (openRevision ? expandRevisionItems(openRevision, c?.plan || {}) : []),
        [openRevision, c?.plan],
    );
    const revisionItemsBySection = useMemo(
        () => groupBySection(revisionRows),
        [revisionRows],
    );
    // Decisions keyed by row.key → { kind: "accepted"|"denied", rationale?, applied_value? }
    const [revisionDecisions, setRevisionDecisions] = useState({});
    // Reset decisions when the revision_request id changes (new round).
    useEffect(() => {
        setRevisionDecisions({});
    }, [openRevision?.id]);
    const decideRevision = useCallback((key, decision) => {
        setRevisionDecisions((prev) => ({ ...prev, [key]: decision }));
    }, []);
    const clearRevisionDecision = useCallback((key) => {
        setRevisionDecisions((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    /* iter98.89 — Reply note (typed in the sticky resubmit footer in
       the Plan tab). Sent as `note` on resubmit; surfaces on the
       doctor's ReviewApprovalPage as the planner's actioned_note. */
    const [revisionReplyNote, setRevisionReplyNote] = useState("");
    const [revisionSubmitting, setRevisionSubmitting] = useState(false);
    const revisionAddressedCount = useMemo(
        () => Object.keys(revisionDecisions).length,
        [revisionDecisions],
    );
    const revisionAllAddressed = revisionRows.length > 0 && revisionAddressedCount >= revisionRows.length;

    /* "Open in Author the plan →" — fired from the Doctor's Submission
       tab's slim summary banner. Flips status revision_requested →
       in_planning (if needed) so PlanTab's "Accept the case" guard
       doesn't block authoring, then switches to the Plan tab. */
    const openPlanForRevision = useCallback(async () => {
        try {
            if (c?.status === "revision_requested") {
                await api.put(`/planner/cases/${c.id}/start`);
            }
            setTab("plan");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to open plan editor");
        }
    }, [c?.id, c?.status]);  // eslint-disable-line react-hooks/exhaustive-deps

    const handleRevisionResubmit = useCallback(async () => {
        if (!openRevision || !revisionAllAddressed) return;
        setRevisionSubmitting(true);
        try {
            const decisions = Object.entries(revisionDecisions).map(([key, d]) => ({
                key,
                decision: d.kind,
                rationale: d.kind === "denied" ? (d.rationale || "") : undefined,
                applied_value: d.kind === "accepted" ? (d.applied_value ?? null) : undefined,
            }));
            const acceptedKeys = decisions.filter((d) => d.decision === "accepted").map((d) => d.key);
            const noteToSend = revisionReplyNote.trim()
                || `Planner resubmitted iteration ${openRevision.iteration || 1} (${acceptedKeys.length} accepted, ${decisions.length - acceptedKeys.length} denied).`;
            await api.post(
                `/cases/${c.id}/revision-requests/${openRevision.id}/resubmit`,
                {
                    actioned_items: acceptedKeys,   // back-compat
                    decisions,                       // new per-row decisions
                    note: noteToSend,
                },
            );
            toast.success("Revised plan sent to doctor");
            setRevisionReplyNote("");
            setRevisionDecisions({});
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to resubmit");
        } finally {
            setRevisionSubmitting(false);
        }
    }, [openRevision, revisionAllAddressed, revisionDecisions, revisionReplyNote, c?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

    /* When the doctor has resubmitted after our info-request, highlight the
       files they uploaded since we paused the case. Boundary is the most
       recent `info_requested` timeline event; anything uploaded at or
       after that timestamp is a "new since you asked" record.

       We deliberately key this off the timeline (not the `info_provided_at`
       sidecar) so the badge persists for the whole in_planning cycle —
       not just the first page-view after the doctor's resubmit. Otherwise
       the planner who opens the case once, navigates away, and comes back
       would lose the visual cue mid-plan. The boundary only resets when
       the planner pauses the case again (new info_requested entry). */
    const infoRequestBoundary = useMemo(() => {
        const tl = c?.timeline || [];
        const last = [...tl].reverse().find((e) => e.action === "info_requested");
        return last?.created_at || null;
    }, [c?.timeline]);

    const newSinceInfoRequest = useMemo(() => {
        if (!infoRequestBoundary || !c) return new Set();
        const cutoff = new Date(infoRequestBoundary).getTime();
        const out = new Set();
        for (const f of (c.files || [])) {
            const t = new Date(f.created_at || 0).getTime();
            if (!Number.isNaN(t) && t >= cutoff) out.add(f.id);
        }
        return out;
    }, [c, infoRequestBoundary]);

    if (loading && !c) {
        return (
            <div className="text-muted-foreground text-sm py-12 text-center" data-testid="planner-case-loading">
                Loading case…
            </div>
        );
    }
    if (!c) return null;

    const status = STATUS_PILLS[c.status] || STATUS_PILLS.submitted;
    const files = c.files || [];
    const scans = files.filter((f) => SCAN_KINDS.includes(f.kind));
    const photos = files.filter((f) => PHOTO_KINDS.includes(f.kind));
    const xrays = files.filter((f) => XRAY_KINDS.includes(f.kind));
    /* iter98.99br — Anything in the case_files table that isn't a photo,
       scan, X-ray, or planner-side setup file lands in the Submission
       tab's Documents tab (medical history forms, consent forms,
       referral letters, etc.). */
    const documents = files.filter((f) => !SCAN_KINDS.includes(f.kind)
        && !PHOTO_KINDS.includes(f.kind)
        && !XRAY_KINDS.includes(f.kind)
        && !SETUP_KINDS.includes(f.kind)
        && f.kind !== "plan_video"
        && f.kind !== "plan_pdf");
    const setupFiles = files.filter((f) => SETUP_KINDS.includes(f.kind));
    const planVideo = files.find((f) => f.kind === "plan_video");
    const hasPlanDeliverable = c.plan?.external_link || c.plan?.video_file_id || planVideo;
    /* The Author tab is "locked" — i.e. shown with a lock icon — for any
       status that pre-dates the planner accepting the case. Once they click
       Start Planning, the status moves to in_planning and the tab unlocks. */
    const PLAN_UNLOCKED_STATUSES = new Set([
        "in_planning", "submitted_to_qa", "sent_for_approval", "ready_for_review",
        "approved", "info_requested",
    ]);
    const planTabLocked = !PLAN_UNLOCKED_STATUSES.has(c.status);

    /* iter98.99ch — MFG Spec tab visibility.
       Only relevant for CoE-planned cases (Self-Plan flows don't have a
       planner; doctor approves and the case goes straight to admin).
       iter98.99ck — Tab is now ALWAYS visible once the case has a planner
       assigned, mirroring the "Author the plan" pattern. It's locked
       (with a Lock icon) until the doctor approves the clinical plan
       and the status flips to `approved_pending_mfg`. */
    const showMfgTab = !!c.planner_id
        && (c.planning_mode || "coe") === "coe";
    const MFG_EDITABLE_STATUSES = new Set(["approved_pending_mfg"]);
    const mfgTabLocked = !MFG_EDITABLE_STATUSES.has(c.status)
        && c.status !== "awaiting_production_assignment"
        && c.status !== "assigned_to_production"
        && c.status !== "in_production"
        && c.status !== "shipped"
        && c.status !== "in_treatment"
        && c.status !== "treatment_started"
        && c.status !== "refinement_requested"
        && c.status !== "completed";

    const start = async () => {
        setBusy(true);
        try {
            await api.put(`/planner/cases/${c.id}/start`);
            toast.success("Case accepted — opening Author the plan");
            await new Promise((r) => setTimeout(r, 100));
            setQueryNote("");
            setTab("plan");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally { setBusy(false); }
    };

    /* Inline ADR — pauses the case and sends the planner's query to the
       doctor in one click, without opening a modal. Validates the textarea
       to make sure we don't ship empty queries.
       iter98.76 — On success, surface the premium "Query sent · case
       paused" dialog (amber tone) instead of a sonner toast, so the
       planner gets a clear walkthrough of what happens next. Navigation
       is deferred until they click the dialog's primary CTA. */
    const requestMoreInfo = async () => {
        const note = queryNote.trim();
        if (!note) {
            toast.error("Tell the doctor what's missing or unclear first.");
            return;
        }
        setBusy(true);
        try {
            await api.put(`/planner/cases/${c.id}/request-info`, { note });
            setQueryNote("");
            setConfirmDialog({ open: true, flow: "planner_query_to_doctor" });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    const sendForReview = async () => {
        /* iter98.99cf — All client-side blockers removed per user
           direction. The planner can submit a plan to the doctor in
           whatever state the editor is in. Backend never enforced
           plan-completeness either; the previous gates were strictly
           a soft-prompt for the planner. Tier / deliverable / review
           note are now all optional. */
        setBusy(true);
        try {
            const payload = { text: reviewNote };
            if (selectedTierId) payload.tier_id = selectedTierId;
            if (selectedPriorityId) payload.priority_tier_id = selectedPriorityId;
            await api.put(`/planner/cases/${c.id}/ready`, payload);
            setReviewNote("");
            /* iter98.76 — surface the premium "Plan sent for approval"
             * dialog instead of a thin toast. Defer navigation to the
             * dialog CTAs so the planner can choose: open the case
             * timeline or return to their queue. */
            setConfirmDialog({ open: true, flow: "plan_sent_to_doctor" });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally { setBusy(false); }
    };

    const uploadSetup = async (filesToUpload) => {
        if (!filesToUpload?.length) return;
        setUploading(true);
        try {
            for (const f of filesToUpload) {
                const fd = new FormData();
                fd.append("file", f);
                await api.post(`/cases/${c.id}/upload?kind=setup`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }
            toast.success("Setup files uploaded");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            if (fileInput.current) fileInput.current.value = "";
        }
    };

    const initials = (c.patient_name || "?")
        .split(/\s+/).filter(Boolean).slice(0, 2)
        .map((w) => w[0]?.toUpperCase()).join("") || "?";

    const openAllForPatient = c.patient_code
        ? `/coe/planner/queue?q=${encodeURIComponent(c.patient_code)}`
        : null;

    return (
        <div data-testid="planner-case-detail" className="space-y-5">
            {/* Top bar — Back to queue + PDF dropdown + Open in Lab Chat */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <Link
                    to={backLink()}
                    className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 font-medium"
                    data-testid="planner-back"
                >
                    <ArrowLeft size={13} weight="bold" /> Back to queue
                </Link>
                <div className="flex items-center gap-3 flex-wrap">
                    {/* iter97.45 — Mid-plan "Hold & query doctor" — pauses the
                        case and fires the planner's question to the doctor.
                        Only visible when the case is in active authoring
                        (status=in_planning). Pre-acceptance has its own
                        formal Request-more-info card on the submission tab;
                        info_requested already shows the paused banner;
                        later statuses are locked. */}
                    {c.status === "in_planning" && (
                        <HoldAndQueryDialog
                            note={queryNote}
                            setNote={setQueryNote}
                            onSubmit={requestMoreInfo}
                            busy={busy}
                        />
                    )}
                    {/* iter98.99bz — Consolidated "View documents" menu.
                        Replaces the prior pair of stand-alone "View Rx"
                        + "View entire submission" header links with a
                        single dropdown that surfaces every printable
                        artifact.
                        iter101.56 — Planner sees only the two documents
                        that are directly relevant to their work: the
                        Signed Prescription (doctor's authorisation) +
                        the Treatment Plan (the deliverable they're
                        authoring). Submission-bundle, Dossier and
                        Sticker are hidden — the planner doesn't need
                        the doctor's raw submission bundle (they read
                        the case body for that), and the sticker is a
                        production artefact. */}
                    <CasePdfMenu
                        caseId={c.id}
                        testIdPrefix="planner-case-pdf"
                        label="View documents"
                        signedRxAvailable={!!c.prescription_compliance?.signed_at}
                        omit={["submission", "dossier", "sticker"]}
                    />
                </div>
            </div>

            {/* HERO — iter101.52 · Unified "Command Bar" header.
                Replaces the prior 3-row stacked layout (identity band →
                status pill row → chip row) with a single integrated
                editorial surface that combines Patient identity,
                Provider/Clinic assignment, Status capsule and the
                cockpit data strip. See PlannerCaseCommandHeader.jsx
                for depth tokens, typography rhythm, and grid math. */}
            <PlannerCaseCommandHeader
                caseObj={c}
                initials={initials}
                openAllForPatient={openAllForPatient}
            />

            {/* ADR — case is paused */}
            {c.status === "info_requested" && (
                <div
                    className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm"
                    data-testid="planner-case-adr-banner"
                >
                    <div className="font-semibold text-amber-900">You've paused this case — waiting on the doctor</div>
                    {c.info_request_note && (
                        <div className="text-amber-900/80 text-xs mt-1">Your request: "{c.info_request_note}"</div>
                    )}
                    <div className="text-amber-900/80 text-xs mt-1">
                        The doctor sees this in their "Awaiting your input" bucket. They can resubmit with the missing info.
                    </div>
                </div>
            )}

            {/* DOCTOR RESPONDED — surfaces when the doctor has resubmitted
                after providing the info we asked for. `last_info_request_note`
                holds what we originally asked so we can show "you asked X →
                they responded" side-by-side. We auto-acknowledge on mount
                so the queue badge clears; the banner stays for this page
                load to preserve context. */}
            {c.info_provided_at && c.status === "in_planning" && (
                <div
                    className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm space-y-2"
                    data-testid="planner-case-response-banner"
                >
                    <div className="flex items-center gap-2">
                        <ChatCircleDots size={16} weight="fill" className="text-emerald-700" />
                        <div className="font-display text-sm font-bold tracking-[0.16em] uppercase text-emerald-900">
                            Doctor responded
                        </div>
                        <span className="text-[10px] tabular-nums text-emerald-800/80 ml-auto">
                            {fmtDate(c.info_provided_at)}
                        </span>
                    </div>
                    {c.last_info_request_note && (
                        <div className="rounded-lg bg-white/60 border border-emerald-100 px-3 py-2 text-xs text-foreground/80">
                            <span className="font-semibold text-emerald-900">You asked: </span>
                            <span className="italic">&ldquo;{c.last_info_request_note}&rdquo;</span>
                        </div>
                    )}
                    {/* iter97.46 — doctor's free-text reply persisted from the
                        Reply & resubmit Dialog. Sits directly under "You
                        asked: …" so the planner reads Q + A in one glance
                        without opening Provider Chat. Hidden if the doctor
                        resubmitted without typing anything. */}
                    {c.last_info_response_note && (
                        <div
                            className="rounded-lg bg-white/60 border border-emerald-100 px-3 py-2 text-xs text-foreground/80"
                            data-testid="planner-case-doctor-response"
                        >
                            <span className="font-semibold text-emerald-900">Doctor replied: </span>
                            <span className="whitespace-pre-wrap">{c.last_info_response_note}</span>
                        </div>
                    )}
                    <div className="text-xs text-emerald-900/80">
                        The doctor has uploaded the records and notes you requested.
                        Review the Submission tab — replaced files appear in the records grid, and any longer thread is in <span className="font-semibold">Provider Chat</span>.
                    </div>
                </div>
            )}

            {/* TABS — iter101.53 · Workspace Deck. Rendered by
                PlannerWorkspaceDeck; extends the command-header
                visual language (teal accents, caps kicker, icon
                discs) so the top of the page reads as one cohesive
                command surface. Attention states preserved: cyan
                unread ping on Submission, amber lock on Plan/MFG
                until unlocked, "MFG SPEC PENDING" caps chip when
                the case has just been approved by the doctor. */}
            <Tabs value={tab} onValueChange={setTab} className="space-y-4" data-testid="planner-case-tabs">
                <PlannerWorkspaceDeck
                    icons={{
                        submission: ClipboardText,
                        plan: PencilSimple,
                        mfg: Wrench,
                        chat: ChatCircleDots,
                        notes: NotePencil,
                        timeline: ClockClockwise,
                    }}
                    unreadFromDoctor={!!c.unread_from_doctor}
                    planLocked={planTabLocked}
                    showMfgTab={showMfgTab}
                    mfgLocked={mfgTabLocked}
                    mfgBadge={c.status === "approved_pending_mfg" ? "Pending" : null}
                />

                {/* TAB 1 — Doctor's submission */}
                <TabsContent value="submission" className="space-y-5">
                    <SubmissionTab
                        c={c}
                        scans={scans}
                        photos={photos}
                        xrays={xrays}
                        documents={documents}
                        newSinceInfoRequest={newSinceInfoRequest}
                        queryNote={queryNote}
                        setQueryNote={setQueryNote}
                        onStart={start}
                        onRequestInfo={requestMoreInfo}
                        busy={busy}
                        openRevision={openRevision}
                        revisionRows={revisionRows}
                        revisionAddressedCount={revisionAddressedCount}
                        onOpenPlanForRevision={openPlanForRevision}
                    />
                </TabsContent>

                {/* TAB 2 — Author the plan */}
                <TabsContent value="plan" className="space-y-5">
                    <PlanTab
                        c={c}
                        files={files}
                        setupFiles={setupFiles}
                        planVideo={planVideo}
                        hasPlanDeliverable={hasPlanDeliverable}
                        reviewNote={reviewNote}
                        setReviewNote={setReviewNote}
                        onSend={sendForReview}
                        onStart={start}
                        onSwitchToSubmission={() => setTab("submission")}
                        onUploadSetup={uploadSetup}
                        fileInputRef={fileInput}
                        uploading={uploading}
                        busy={busy}
                        onPlanSaved={load}
                        tiersList={tiersList}
                        priorityTiers={priorityTiers}
                        selectedTierId={selectedTierId}
                        setSelectedTierId={setSelectedTierId}
                        onManualTierSelect={handleManualTierSelect}
                        tierAutoSelected={tierAutoSelected}
                        selectedPriorityId={selectedPriorityId}
                        setSelectedPriorityId={setSelectedPriorityId}
                        openRevision={openRevision}
                        revisionRows={revisionRows}
                        revisionItemsBySection={revisionItemsBySection}
                        revisionDecisions={revisionDecisions}
                        onRevisionDecide={decideRevision}
                        onRevisionClearDecision={clearRevisionDecision}
                        revisionReplyNote={revisionReplyNote}
                        setRevisionReplyNote={setRevisionReplyNote}
                        revisionAllAddressed={revisionAllAddressed}
                        revisionAddressedCount={revisionAddressedCount}
                        revisionSubmitting={revisionSubmitting}
                        onRevisionResubmit={handleRevisionResubmit}
                    />
                </TabsContent>

                {/* TAB 3 — Manufacturing Spec (post-doctor-approval planner work) */}
                {showMfgTab && (
                    <TabsContent value="mfg" className="space-y-5">
                        <ManufacturingSpecForm
                            caseId={c.id}
                            c={c}
                            onSubmitted={() => load()}
                        />
                    </TabsContent>
                )}

                {/* TAB 4 — Provider Chat (case-scoped) */}
                <TabsContent value="chat" className="space-y-5">
                    <CaseChatThread caseId={c.id} role="planner" />
                </TabsContent>

                {/* TAB 5 — My notes (materials panel moved to Production portal) */}
                <TabsContent value="notes" className="space-y-5">
                    <PlannerNotesTab caseId={c.id} />
                </TabsContent>

                {/* TAB 6 — Activity timeline */}
                <TabsContent value="timeline" className="space-y-5">
                    <CaseTimelineTab timeline={c.timeline || []} viewerRole="planner" />
                </TabsContent>
            </Tabs>
            {/* iter98.76 — Universal premium notification dialog. Shown
                after `sendForReview` (Plan sent for approval, teal tone)
                and `requestMoreInfo` (Query sent · case paused, amber
                tone). Primary CTA → opens the case page; secondary →
                back to the planner's queue. */}
            <CaseSubmittedDialog
                open={confirmDialog.open}
                flow={confirmDialog.flow || "plan_sent_to_doctor"}
                caseCode={c.code || ""}
                primaryLabel={
                    confirmDialog.flow === "planner_query_to_doctor"
                        ? "Back to queue"
                        : "Back to queue"
                }
                secondaryLabel="Stay on case"
                onPrimary={() => {
                    setConfirmDialog({ open: false, flow: null });
                    navigate(backLink());
                }}
                onSecondary={() => {
                    setConfirmDialog({ open: false, flow: null });
                    load();   // refresh status pill / timeline inline
                }}
            />
        </div>
    );
}


/* =====================================================================
 * Tab 1 — Doctor's submission
 * The decision panel (Accept / Ask for info) now lives inline at the
 * bottom of this tab — the planner's natural reading flow is "review
 * the doctor's submission → decide". The activity timeline lives in
 * its own tab. The right rail is gone — full-width workspace.
 * ===================================================================== */

/* iter98.99cd — BriefAndRecords: small wrapper that tracks the live
   section count from PlannerCaseBriefCard (bubbled up via
   `onSectionCount`) and uses it to compute the correct numbers for
   the per-tooth accordion + records accordion that follow. Single
   source of truth — visibility logic stays inside the brief card. */
function BriefAndRecords({ c, scans, photos, xrays, documents, newSinceInfoRequest }) {
    const [briefCount, setBriefCount] = useState(1);
    const hasPerTooth = !!(
        ((c?.plan?.tooth_movements_plan || c?.prescription?.tooth_movements_plan) || []).length
        || ((c?.plan?.attachments_plan || c?.prescription?.attachments_plan) || []).length
        || ((c?.plan?.precision_cuts || c?.prescription?.precision_cuts) || []).length
        || (c?.plan?.bite_ramps?.enabled || c?.prescription?.bite_ramps?.enabled)
    );
    const perToothNumber = briefCount + 1;
    const recordsNumber = briefCount + (hasPerTooth ? 2 : 1);
    return (
        <>
            <PlannerCaseBriefCard c={c} startNumber={1} onSectionCount={setBriefCount} />
            <PlannerPerToothCard c={c} number={perToothNumber} />
            <PlannerSubmissionRecords
                scans={scans}
                photos={photos}
                xrays={xrays}
                documents={documents}
                newSinceInfoRequest={newSinceInfoRequest}
                caseId={c?.id}
                number={recordsNumber}
            />
        </>
    );
}


function SubmissionTab({
    c, scans, photos, xrays, documents, newSinceInfoRequest, queryNote, setQueryNote,
    onStart, onRequestInfo, busy,
    openRevision, revisionRows, revisionAddressedCount, onOpenPlanForRevision,
}) {
    const newCount = newSinceInfoRequest?.size || 0;
    return (
        <div className="space-y-5" data-testid="planner-submission-tab-body">
            {/* iter98.89 — Slim summary banner when there's an open
                revision_request. Per-row actioning has moved INLINE
                into the Author the plan tab (each affected section
                renders a RevisionInlineBanner above its accordion).
                This banner is the doorway: shows the overall context
                and routes the planner straight into the editor.
                For closed (actioned) revisions or no revisions at all,
                the legacy RevisionRequestPanel handles the rendering
                (history, free-form notes, etc.). */}
            {openRevision ? (
                <RevisionInlineSummary
                    revision={openRevision}
                    totalRows={revisionRows.length}
                    addressedCount={revisionAddressedCount}
                    onOpenPlanForRevision={onOpenPlanForRevision}
                />
            ) : (
                <RevisionRequestPanel caseId={c.id} caseStatus={c.status} />
            )}

            {/* Banner pinned to the top whenever the doctor has resubmitted
                with new files since the last info-request. */}
            {newCount > 0 && (
                <div
                    className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-xs text-emerald-900 flex items-center gap-2"
                    data-testid="planner-submission-newfiles-banner"
                >
                    <ArrowsClockwise size={14} weight="fill" className="text-emerald-700 shrink-0" />
                    <span className="font-semibold">{newCount} record{newCount === 1 ? "" : "s"} replaced since your info-request</span>
                    <span className="text-emerald-800/80">— look for the green "Replaced" badge below.</span>
                </div>
            )}

            {/* iter98.99br — Flag alerts (extractions, perio, CBCT) pinned
                above the main grid so they get the planner's attention
                before they start reading the brief. */}
            <PlannerSubmissionFlagAlert c={c} />

            {/* iter101.56 — Submission tab wrapper adopts the exact
                same header aesthetic as Author-the-plan (caps title
                + italic subhead + gray strip bg) per user direction. */}
            <WorkspaceSection
                title="Doctor's Submission"
                subhead="Review the case brief and either accept or query the doctor."
                testid="planner-submission-workspace"
            >
                <div className="space-y-4">
                    <BriefAndRecords
                        c={c}
                        scans={scans}
                        photos={photos}
                        xrays={xrays}
                        documents={documents}
                        newSinceInfoRequest={newSinceInfoRequest}
                    />

                    {/* Decision panel — Accept the case OR query the doctor.
                        Lives at the end of the brief reading flow. */}
                    <DecisionPanel
                        c={c}
                        queryNote={queryNote}
                        setQueryNote={setQueryNote}
                        onStart={onStart}
                        onRequestInfo={onRequestInfo}
                        busy={busy}
                    />
                </div>
            </WorkspaceSection>
        </div>
    );
}


/* Sticky decision card in the Submission tab's right rail. Lets the planner
   either accept the case (Start planning) or send queries to the doctor and
   pause the case (Request more info). State machine: */
function DecisionPanel({ c, queryNote, setQueryNote, onStart, onRequestInfo, busy }) {
    const canStart = ["assigned_to_planner", "refinement_requested", "revision_requested"].includes(c.status);
    const banner = c.unread_from_doctor ? <UnreadDoctorBanner u={c.unread_from_doctor} caseId={c.id} /> : null;

    /* iter98.99bx — Acceptance attestation. Mirrors the doctor's
       "I have read the plan" gate from PlanIterationsStack. The
       planner must explicitly tick that they've reviewed the
       doctor's submission before they can claim the case. The
       checkbox is per-case and scoped to component lifetime (it
       resets when the planner navigates away — that's by design;
       accidental re-acceptance shouldn't be a single-click action). */
    const [reviewed, setReviewed] = useState(false);

    // Case in flight — show a calm "accepted" affirmation. iter97.45 — the
    // post-acceptance "Spotted something missing?" card moved out of this
    // panel entirely. The mid-plan pause workflow is now a header-level
    // "Hold & query doctor" Dialog (always-on-page, less cluttered) so the
    // submission tab can stay focused on its real job: doctor → planner
    // handoff context.
    if (c.status === "in_planning") {
        return (
            <div className="space-y-3">
                {banner}
                <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4" data-testid="planner-decision-accepted">
                    <div className="flex items-center gap-2">
                        <CheckCircle size={16} weight="fill" className="text-emerald-700" />
                        <h3 className="font-display text-sm font-bold tracking-[0.16em] uppercase text-emerald-900">
                            Case accepted
                        </h3>
                    </div>
                    <p className="text-xs text-emerald-900/80 mt-1.5 leading-relaxed">
                        You&apos;ve accepted this case. Switch to <span className="font-semibold">Author the plan</span> to attach the setup and send it to the doctor. If you spot a blocker mid-plan, use <span className="font-semibold">Hold &amp; query doctor</span> in the page header above.
                    </p>
                </div>
            </div>
        );
    }
    if (c.status === "info_requested") {
        return (
            <div className="space-y-3">
                {banner}
                <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4" data-testid="planner-decision-paused">
                    <div className="flex items-center gap-2">
                        <ChatCircleDots size={16} weight="fill" className="text-amber-700" />
                        <h3 className="font-display text-sm font-bold tracking-[0.16em] uppercase text-amber-900">
                            Waiting on doctor
                        </h3>
                    </div>
                    <p className="text-xs text-amber-900/80 mt-1.5 leading-relaxed">
                        Case is paused. The doctor sees your query in their "Awaiting your input" bucket.
                    </p>
                    {c.info_request_note && (
                        <p className="text-xs text-amber-900 mt-2 italic line-clamp-4">
                            "{c.info_request_note}"
                        </p>
                    )}
                </div>
            </div>
        );
    }
    /* iter101.48 — Planner is DONE with the case once the plan is under
       review (submitted_to_qa / sent_for_approval / ready_for_review),
       once the doctor has approved (approved / approved_pending_mfg),
       AND once the planner has submitted the MFG spec (case then flips
       to awaiting_production_assignment). All downstream production
       statuses (submitted_to_production, in_production, shipped,
       completed) should ALSO show the locked banner so the "Take your
       call" hero never reappears on a case that is no longer the
       planner's problem. Previously only 4 statuses were listed here,
       so once a planner sent MFG to admin the "Accept & start planning
       / Send query" decision hero came back on the Submission tab — a
       clear regression flagged by the user in iter101.48. */
    const PLANNER_HANDED_OFF = [
        "submitted_to_qa",
        "sent_for_approval",
        "ready_for_review",
        "approved",
        "approved_pending_mfg",
        "awaiting_production_assignment",
        "submitted_to_production",
        "in_production",
        "shipped",
        "completed",
    ];
    if (PLANNER_HANDED_OFF.includes(c.status)) {
        // iter101.58 — STATUS card removed per user direction. When
        // the case has moved beyond the planner (approved / MFG /
        // production …), the tab header already communicates status
        // via the case-header pill + tab-body banner. This redundant
        // "STATUS · MFG spec submitted…" card added noise without
        // signal. If a banner has been raised (e.g. plan-revision
        // digest), we still render it so no info is lost.
        return banner ? <div>{banner}</div> : null;
    }

    /* Default actionable state — case is "submitted" / "refinement_requested" /
       "revision_requested". Premium "Take your call" hero with two
       distinct paths: start planning (primary) OR pause + query the
       doctor (orange). iter98.99bw — Full-width hero panel per user
       direction, replacing the prior stacked-cards layout. */
    const canRequest = queryNote.trim().length > 0 && !busy;
    return (
        <div className="space-y-3" data-testid="planner-decision-panel">
            {banner}

            <div
                className="relative rounded-2xl bg-card border border-border overflow-hidden shadow-[0_1px_0_rgba(0,0,0,0.02)]"
                data-testid="planner-decision-hero"
            >
                {/* Hero ribbon — accent bar + label + headline */}
                <div className="px-5 sm:px-7 pt-5 pb-3 bg-gradient-to-br from-secondary/40 via-card to-card border-b border-border">
                    <div className="flex items-center gap-2">
                        <span className="inline-block w-[3px] h-4 rounded-sm bg-primary shrink-0 hidden" aria-hidden />
                        <span className="text-[11px] font-bold tracking-[0.16em] uppercase text-muted-foreground">
                            Decision
                        </span>
                    </div>
                    <h2
                        className="font-display text-2xl sm:text-[26px] font-bold tracking-tight text-foreground mt-1.5"
                        data-testid="planner-decision-title"
                    >
                        Take your call
                    </h2>
                    <p className="text-[13px] text-muted-foreground mt-1 max-w-2xl">
                        You've read through the doctor's submission. Pick a path below — either accept the case and start authoring, or pause and ask the doctor a clarifying question first.
                    </p>
                </div>

                {/* Two-branch decision body */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] items-stretch">
                    {/* PATH 1 — Start planning (primary). */}
                    <div className="p-5 sm:p-6 flex flex-col" data-testid="planner-decision-start-card">
                        <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0 ring-1 ring-primary/15">
                                <PlayCircle size={18} weight="fill" />
                            </div>
                            <div className="min-w-0">
                                <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-primary">
                                    Path A · Accept
                                </div>
                                <h3 className="font-display text-base font-bold text-foreground mt-0.5">
                                    Start planning
                                </h3>
                            </div>
                        </div>
                        <p className="text-[12.5px] text-muted-foreground leading-relaxed mt-3">
                            Claim this case and unlock the <span className="font-semibold text-foreground">Author the plan</span> tab. The case is yours from this point — you can pause it anytime later from the page header.
                        </p>
                        <div className="mt-auto pt-4 space-y-3">
                            {/* iter98.99bx — Attestation gate, mirrors the
                                doctor's "I have read the plan" pattern in
                                PlanIterationsStack so both sides confirm
                                they've reviewed before locking in a state
                                change. */}
                            <label
                                className="flex items-start gap-2.5 cursor-pointer rounded-lg border border-primary/20 bg-primary/5 px-3 py-2.5"
                                data-testid="planner-decision-attestation"
                            >
                                <Checkbox
                                    checked={reviewed}
                                    onCheckedChange={(v) => setReviewed(v === true)}
                                    data-testid="planner-decision-attestation-checkbox"
                                    className="mt-0.5 shrink-0"
                                />
                                <span className="text-[12px] text-foreground/85 leading-snug">
                                    <span className="font-semibold">I have reviewed the doctor&apos;s submission</span> — records, clinical assessment and prescription are sufficient for me to author a treatment plan.
                                </span>
                            </label>
                            <Button
                                onClick={onStart}
                                disabled={busy || !canStart || !reviewed}
                                size="lg"
                                className="w-full rounded-full text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="planner-decision-start"
                                title={reviewed ? undefined : "Tick the review confirmation above to enable accept"}
                            >
                                <PlayCircle size={15} className="mr-2" weight="fill" />
                                Accept &amp; start planning
                            </Button>
                        </div>
                    </div>

                    {/* OR divider — vertical on desktop, horizontal on mobile */}
                    <div className="flex items-center justify-center lg:flex-col py-0 lg:py-6 lg:px-0">
                        <div className="hidden lg:block w-px flex-1 bg-border" aria-hidden />
                        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground/60 px-3 py-1 bg-card border border-border rounded-full">
                            or
                        </span>
                        <div className="hidden lg:block w-px flex-1 bg-border" aria-hidden />
                        <div className="lg:hidden flex-1 h-px bg-border" aria-hidden />
                    </div>

                    {/* PATH 2 — Need clarification (orange). */}
                    <div className="p-5 sm:p-6 flex flex-col" data-testid="planner-decision-request-card">
                        <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-full bg-orange-100 text-orange-700 grid place-items-center shrink-0 ring-1 ring-orange-200">
                                <Question size={18} weight="fill" />
                            </div>
                            <div className="min-w-0">
                                <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-orange-700">
                                    Path B · Query
                                </div>
                                <h3 className="font-display text-base font-bold text-foreground mt-0.5">
                                    Need clarification?
                                </h3>
                            </div>
                        </div>
                        <p className="text-[12.5px] text-muted-foreground leading-relaxed mt-3">
                            Write what's missing or unclear. We'll send it to the doctor and pause the case until they respond — they see it in their <span className="font-semibold text-foreground">Awaiting your input</span> bucket.
                        </p>
                        <div className="mt-3">
                            <Label htmlFor="planner-decision-query" className="text-[10px] font-bold tracking-[0.14em] uppercase text-muted-foreground">
                                Your queries for the doctor
                            </Label>
                            <Textarea
                                id="planner-decision-query"
                                rows={4}
                                value={queryNote}
                                onChange={(e) => setQueryNote(e.target.value)}
                                placeholder="e.g. Please share lateral cephalogram + bite registration. Confirm if extractions are on UR4 or UR5."
                                className="resize-none text-sm mt-1.5"
                                data-testid="planner-decision-query"
                                disabled={busy}
                            />
                        </div>
                        <div className="mt-auto pt-3">
                            <Button
                                onClick={onRequestInfo}
                                disabled={!canRequest}
                                variant="outline"
                                size="lg"
                                className="w-full rounded-full text-sm font-semibold text-orange-700 border-orange-300 hover:bg-orange-50 hover:text-orange-800 disabled:opacity-40 disabled:cursor-not-allowed"
                                data-testid="planner-decision-request-info"
                            >
                                <Question size={15} className="mr-2" weight="fill" />
                                Send query &amp; pause case
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}


/* =====================================================================
 * Tab 2 — Author the plan
 * ===================================================================== */

/* iter98.99cg — Tiny inline warning that surfaces when the planner has
   authored an aligner count, but no active tier covers that range. We
   keep this read-only and informational; the tier picker remains
   unblocked so a human can override the gap. */
function TierMatchWarning({ plan, tiers = [], selected }) {
    if (!plan || !tiers.length) return null;
    const up = Number(plan.aligner_count_upper || 0);
    const lo = Number(plan.aligner_count_lower || 0);
    const total = Math.max(up, lo);
    if (total <= 0) return null;
    const inRange = tiers.some((t) => {
        const tMin = Number(t.aligner_min || 0);
        const tMax = Number(t.aligner_count || 0);
        return tMax > 0 && total >= tMin && total <= tMax;
    });
    if (inRange) return null;
    // Compute helpful guidance — show the closest tier on either side.
    const sorted = [...tiers]
        .filter((t) => Number(t.aligner_count || 0) > 0)
        .sort((a, b) => Number(a.aligner_min || 0) - Number(b.aligner_min || 0));
    const minOverall = sorted.length ? Number(sorted[0].aligner_min || 0) : 0;
    const maxOverall = sorted.length ? Number(sorted[sorted.length - 1].aligner_count || 0) : 0;
    /* iter98.99da — Out-of-range message rewritten to use "stages" so the
       planner reads the value as 15 stages of treatment (15 sets of U+L),
       not 15 physical aligner pieces. The math is identical
       (max(upper, lower) = stages). */
    let msg = `${total} treatment stage${total === 1 ? "" : "s"} doesn't match any active tier.`;
    if (total < minOverall) msg += ` Smallest tier starts at ${minOverall} stages.`;
    else if (total > maxOverall) msg += ` Largest tier caps at ${maxOverall} stages.`;
    return (
        <div
            className="flex items-start gap-2 px-2.5 py-1.5 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-800"
            data-testid="planner-tier-out-of-range"
        >
            <WarningCircle size={14} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
            <span>
                {msg} {!selected && <em>Pick a tier manually before submitting.</em>}
            </span>
        </div>
    );
}


function PlanTab({
    c, files, setupFiles, planVideo, hasPlanDeliverable,
    reviewNote, setReviewNote, onSend, onSwitchToSubmission,
    onUploadSetup, fileInputRef, uploading, busy, onPlanSaved,
    tiersList = [], priorityTiers = [],
    selectedTierId, setSelectedTierId, selectedPriorityId, setSelectedPriorityId,
    onManualTierSelect, tierAutoSelected = false,
    openRevision, revisionRows, revisionItemsBySection, revisionDecisions,
    onRevisionDecide, onRevisionClearDecision,
    revisionReplyNote, setRevisionReplyNote,
    revisionAllAddressed, revisionAddressedCount, revisionSubmitting,
    onRevisionResubmit,
}) {
    /* iter 96 — completion checklist derived from case + plan. Hoisted above
       conditional returns to satisfy React Rules-of-Hooks. */
    const completion = usePlanCompletion(c, files);

    /* iter 96e — autosave badge state, bubbled up from PlanEditorInline via
       onSaveStateChange. Lives on PlanTab so the header badge updates as the
       editor saves. */
    const [saveState, setSaveState] = useState({ status: "idle", at: null, error: null });

    /* iter98.99cy — Submit-to-doctor gate (CoE flow only). A tier MUST be
       picked before the planner can ship to the doctor. Self-Plan cases
       lock the tier at submission so are naturally green. Hoisted here
       so the JSX downstream stays declarative. */
    const tierMissingForSubmit = (c?.planning_mode || "coe") === "coe" && !selectedTierId;

    /* iter98.89 — when the case is in revision_requested and the planner
       already owns it (case has a `planner_id`), let them author the
       revision INLINE. The old guard forced a re-acceptance via the
       Submission tab; with inline authoring we route through
       `openPlanForRevision` which calls /start under the hood. So the
       only blocker here is a brand-new case (assigned_to_planner) which
       still requires the explicit Start click. */
    const guardBlocksRevisionEditing = (
        c.status === "submitted"
        || c.status === "assigned_to_planner"
        || c.status === "refinement_requested"
        || (c.status === "revision_requested" && !c.planner_id)
    );
    if (guardBlocksRevisionEditing) {
        return (
            <div className="bg-card border border-border rounded-2xl p-8 text-center" data-testid="planner-plan-not-started">
                <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 grid place-items-center mb-3">
                    <Lock size={22} weight="duotone" className="text-primary" />
                </div>
                <h3 className="font-display text-lg font-bold">Accept the case to author the plan</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                    Head back to the <span className="font-semibold text-foreground">Doctor's submission</span> tab and click <span className="font-semibold text-foreground">Start planning</span>. You'll be brought back here automatically once the case is yours.
                </p>
                <Button
                    onClick={onSwitchToSubmission}
                    disabled={busy}
                    variant="outline"
                    className="mt-4"
                    data-testid="planner-plan-goto-submission"
                >
                    <ArrowLeft size={14} className="mr-1.5" /> Go to Doctor's submission
                </Button>
            </div>
        );
    }
    if (c.status === "submitted_to_qa" || c.status === "sent_for_approval" || c.status === "ready_for_review") {
        /* iter98.99cn — Instead of locking the planner out behind a
           waiting-screen, render the authored plan in READ-ONLY mode so
           they can still review their own work while the doctor (or QA)
           is reviewing it. Mirrors the Doctor's Submission tab pattern
           — visible, scannable, just not editable. A slim status banner
           pinned at the top sets context. */
        const reviewer = c.status === "submitted_to_qa" ? "QA" : "doctor";
        return (
            <div className="space-y-4" data-testid="planner-plan-readonly-review">
                <div className="rounded-2xl border border-blue-200 bg-blue-50/60 px-5 py-3 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-xl bg-blue-100 grid place-items-center shrink-0 mt-0.5">
                        <Hourglass size={16} weight="duotone" className="text-blue-700" />
                    </div>
                    <div className="flex-1">
                        <div className="font-semibold text-sm text-blue-900">
                            Waiting on {reviewer} review
                        </div>
                        <p className="text-[12px] text-blue-800/85 mt-0.5">
                            Your plan is read-only while it's under review. If they bounce it back you'll be able to revise here.
                        </p>
                    </div>
                </div>
                <PlanEditorInline
                    caseId={c.id}
                    caseStatus={c.status}
                    caseFiles={files}
                    initialPlan={c.plan}
                    readOnly
                    manufacturingSpec={c.manufacturing_spec}
                    caseArches={c.clinical?.arch || c.arches}
                />
            </div>
        );
    }
    if (c.status === "approved" || c.status === "approved_pending_mfg") {
        // iter97.55 — surface the doctor's free-text approval note (set via
        // `ReviewApprovalPage`) prominently on the planner workspace, so the
        // team sees feedback / gratitude without scrolling the timeline tab.
        // iter98.99cn — Show the authored plan in read-only mode underneath
        // the approval banner so the planner can keep referencing it while
        // they work on the MFG spec. Same pattern as the awaiting-review
        // state above.
        const approvalEntry = [...(c.timeline || [])]
            .reverse()
            .find((e) => e.action === "approved" && (e.doctor_note || "").trim());
        const doctorNote = approvalEntry?.doctor_note?.trim() || "";
        const noteAuthor = approvalEntry?.author_name || "Doctor";
        const noteAt = approvalEntry?.created_at ? fmtDate(approvalEntry.created_at) : "";
        const planUnlocked = !!c.plan_unlocked_at;
        /* iter101.57 — Emerald "Approved by doctor" / "Plan unlocked
           by admin" banner removed per user direction. Its context
           sentence merges into the PlanEditorInline header subhead
           and the Locked/Unlocked pill + Unlock Request control
           travels into the header's right slot (next to Expand all).
           The doctor's approval note stays as its own card below
           the header since it's substantive content, not chrome. */
        const headerSubhead = planUnlocked
            ? "Plan unlocked by admin. Saving will replace this version and automatically re-lock."
            : c.status === "approved_pending_mfg"
                ? "Clinical plan locked. Finalise the MFG Spec tab to send this case to admin."
                : "Plan is locked. Case is moving to production.";
        const headerRight = planUnlocked ? (
            <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-300 text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-800"
                data-testid="plan-unlocked-pill"
            >
                <Sparkle size={11} weight="fill" /> Unlocked
            </span>
        ) : (
            <>
                <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 border border-slate-200 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-700"
                    data-testid="plan-locked-pill"
                >
                    <Lock size={11} weight="fill" /> Locked
                </span>
                <UnlockRequestControl caseId={c.id} section="plan" caseObj={c} onChanged={onPlanSaved} />
            </>
        );
        return (
            <div className="space-y-4" data-testid="planner-plan-approved">
                {doctorNote && (
                    <div
                        className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4"
                        data-testid="planner-plan-approval-note"
                    >
                        <div className="flex items-center gap-2 mb-1.5">
                            <ChatCircleDots size={14} weight="duotone" className="text-emerald-700" />
                            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-900">
                                Note from {noteAuthor}
                            </div>
                            {noteAt && (
                                <span className="text-[10px] text-emerald-800/70 ml-auto tabular-nums">{noteAt}</span>
                            )}
                        </div>
                        <p
                            className="text-[13px] text-emerald-950 leading-relaxed whitespace-pre-wrap"
                            data-testid="planner-plan-approval-note-text"
                        >
                            {doctorNote}
                        </p>
                    </div>
                )}
                <PlanEditorInline
                    caseId={c.id}
                    caseStatus={c.status}
                    caseFiles={files}
                    initialPlan={c.plan}
                    readOnly={!planUnlocked}
                    manufacturingSpec={c.manufacturing_spec}
                    caseArches={c.clinical?.arch || c.arches}
                    onSaved={onPlanSaved}
                    headerRight={headerRight}
                    headerSubhead={headerSubhead}
                />
            </div>
        );
    }
    if (c.status === "info_requested") {
        return (
            <div className="bg-card border border-border rounded-2xl p-8 text-center" data-testid="planner-plan-info-requested">
                <div className="mx-auto w-12 h-12 rounded-full bg-orange-50 grid place-items-center mb-3">
                    <Lock size={22} weight="duotone" className="text-orange-700" />
                </div>
                <h3 className="font-display text-lg font-bold">Case paused — waiting on doctor</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                    You can't author the plan until the doctor responds to your ADR request.
                </p>
            </div>
        );
    }

    /* iter101.48 — Once the planner submits the MFG spec the case
       leaves their queue entirely (awaiting_production_assignment)
       and moves through production. Show the plan in read-only mode
       with a calm "handed off" banner, and — critically — HIDE the
       "Submit Plan to Doctor" section (Section 08) so the planner
       cannot re-submit a case that's already left their queue.
       This was regression flagged by the user in iter101.48. */
    const POST_MFG_LOCKED = new Set([
        "awaiting_production_assignment",
        "submitted_to_production",
        "in_production",
        "shipped",
        "completed",
    ]);
    if (POST_MFG_LOCKED.has(c.status)) {
        const bannerCopy = c.status === "awaiting_production_assignment"
            ? "MFG spec submitted. Admin is routing this case to production."
            : c.status === "submitted_to_production" || c.status === "in_production"
                ? "Case is in production. Referencing the plan in read-only mode."
                : c.status === "shipped"
                    ? "Case shipped from production."
                    : "Case completed.";
        const planUnlocked = !!c.plan_unlocked_at;
        /* iter101.57 — Same header consolidation as the approved
           branch: slate banner collapsed into PlanEditorInline
           header subhead + right slot. */
        const headerSubhead = planUnlocked
            ? "Plan unlocked by admin. Saving will replace this version and automatically re-lock."
            : bannerCopy;
        const headerRight = planUnlocked ? (
            <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-300 text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-800"
                data-testid="plan-unlocked-pill"
            >
                <Sparkle size={11} weight="fill" /> Unlocked
            </span>
        ) : (
            <>
                <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 border border-slate-200 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-700"
                    data-testid="plan-locked-pill"
                >
                    <Lock size={11} weight="fill" /> Locked
                </span>
                <UnlockRequestControl caseId={c.id} section="plan" caseObj={c} onChanged={onPlanSaved} />
            </>
        );
        return (
            <div className="space-y-4" data-testid="planner-plan-handed-off">
                <PlanEditorInline
                    caseId={c.id}
                    caseStatus={c.status}
                    caseFiles={files}
                    initialPlan={c.plan}
                    readOnly={!planUnlocked}
                    manufacturingSpec={c.manufacturing_spec}
                    caseArches={c.clinical?.arch || c.arches}
                    onSaved={onPlanSaved}
                    headerRight={headerRight}
                    headerSubhead={headerSubhead}
                />
            </div>
        );
    }

    /* Active in_planning workspace — iter 96 redesign:
       Main column (editor + ancillary cards) + sticky right rail with
       completion checklist & clinical quick reference at lg+. Sticky
       quick-jump nav lives at the top of the main column.
       iter98.75 — Floating PlanReadinessScorecard pinned to the right
       edge so the planner sees overall readiness + advisory issues
       while authoring, with one-click jumps to the relevant section. */
    return (
        <div className="space-y-5">
            <PlanReadinessScorecard caseDoc={c} files={files} />
            <div className="flex items-center justify-between gap-3 flex-wrap">
                {/* iter98.99ce — Demo-fill plan button. One-click
                    populates every accordion (bite metrics → manufacturing
                    spec) so the planner can rapidly showcase a fully
                    authored plan during demos. Hidden in production
                    builds; rotates through 3 templates (mild / moderate
                    / complex) on repeat clicks. */}
                <DemoFillPlanButton caseId={c.id} onFilled={onPlanSaved} />
                <AutosaveBadge status={saveState.status} lastSavedAt={saveState.at} />
            </div>

            <PlanEditorInline
                    caseId={c.id}
                    caseStatus={c.status}
                    caseFiles={files}
                    initialPlan={c.plan}
                    onSaved={onPlanSaved}
                    onSaveStateChange={setSaveState}
                    manufacturingSpec={c.manufacturing_spec}
                    caseArches={c.clinical?.arch || c.arches}
                    revisionItemsBySection={revisionItemsBySection}
                    revisionDecisions={revisionDecisions}
                    onRevisionDecide={onRevisionDecide}
                    onRevisionClearDecision={onRevisionClearDecision}
                    afterSections={(
                /* iter98.99cc — Submit-to-doctor accordion nested INSIDE
                    the PlanEditorInline card via the `afterSections`
                    slot so 08 sits visually under 07 in the same
                    container instead of floating in its own card. */
                <details
                    id="sec-submit"
                    className="group scroll-mt-20 rounded-2xl border border-border/70 bg-card overflow-hidden shadow-[0_1px_0_rgba(15,23,42,0.02),0_6px_18px_-14px_rgba(15,23,42,0.10)]"
                    data-testid="planner-plan-submit-card"
                >
                    <summary
                        className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-teal-50/40 transition-colors cursor-pointer list-none group-open:border-b group-open:border-border/60"
                        style={{ background: "linear-gradient(to right, rgba(20,184,166,0.05) 0%, transparent 55%)" }}
                        data-testid="planner-plan-submit-summary"
                    >
                        <span className="font-display text-[13px] font-bold tabular-nums shrink-0 w-6 text-teal-700">
                            08
                        </span>
                        <span className="grid place-items-center w-5 h-5 shrink-0">
                            <PaperPlaneRight size={14} weight="fill" className="text-emerald-600" />
                        </span>
                        <h3 className="text-[11px] font-bold tracking-[0.14em] uppercase text-teal-900/80 shrink-0">
                            Submit plan to doctor
                        </h3>
                        <span className="ml-3 text-[11px] text-muted-foreground/60 truncate min-w-0 italic normal-case tracking-normal">
                            Ready to hand off?
                        </span>
                        <span className="ml-auto shrink-0">
                            <CaretDown
                                size={13}
                                weight="bold"
                                className="text-teal-700 transition-transform group-open:rotate-180 group-hover:scale-110"
                            />
                        </span>
                    </summary>

                    {/* iter98.99cr — User direction: removed "Plan at a glance" + "Preview doctor view" buttons.
                        Both were redundant — the planner just authored sections 01–07 above this card
                        and can scroll up to review. Cuts decision fatigue right before the high-stakes Submit. */}

                    {/* iter98.99at — Lean tier + submit footer.
                        - Tier card collapsed into the same surface as the
                          submit footer (no separate `mb-5` block + dual
                          column grid).
                        - Manufacturing speed compressed to a tiny inline
                          pill next to the section header (was a full row).
                        - Submit row is a single horizontal line: notes
                          textarea on the left, CTA on the right.
                        - Subtle bg tint preserved for the "ready to ship"
                          feel without dominating the screen. */}
                    <div className="bg-gradient-to-br from-emerald-50/40 via-white to-cyan-50/30 px-5 py-5 space-y-4">
                        {(c.planning_mode || "coe") === "coe" && (
                            <div data-testid="planner-tier-decision-card" className="space-y-2.5">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">
                                            Treatment tier
                                        </Label>
                                        {/* iter98.99cg — Auto-selection hint pill. Surfaces ONLY
                                            when the tier was auto-matched against the aligner
                                            count the planner just authored. Disappears the
                                            moment the planner clicks any card (explicit pick). */}
                                        {tierAutoSelected && selectedTierId && (
                                            <span
                                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-600/10 border border-emerald-300 text-[9px] font-semibold uppercase tracking-[0.14em] text-emerald-700"
                                                data-testid="planner-tier-auto-selected-hint"
                                                title="Tier auto-selected from the aligner count you entered. Click any other card to override."
                                            >
                                                <CheckCircle size={9} weight="fill" />
                                                Auto-matched
                                            </span>
                                        )}
                                    </div>
                                    {/* iter98.99cm — Removed `<DoctorPriorityPill>` from the
                                        Treatment tier header. The doctor's TAT/priority choice
                                        is a commercial concern (manufacturing speed + ₹ fee)
                                        that does not belong on the planner's clinical authoring
                                        surface. It still lives on the doctor & admin sides
                                        where the decision actually originates. */}
                                </div>
                                {/* TIER CARDS — primary action */}
                                <PlannerTierCards
                                    tiers={tiersList}
                                    selectedTierId={selectedTierId}
                                    onSelect={(t) => {
                                        if (onManualTierSelect) onManualTierSelect(t);
                                        else setSelectedTierId(t.id);
                                    }}
                                    autoSelected={tierAutoSelected}
                                />
                                {/* iter98.99cr — Lock-down helper. When the
                                    tier was auto-matched, all non-matching
                                    tiers go disabled (see PlannerTierCards).
                                    This caption explains the locked state so
                                    the planner knows the canonical input is
                                    the aligner count, not the tier card. */}
                                {tierAutoSelected && selectedTierId && (
                                    <div
                                        className="flex items-start gap-2 rounded-lg border border-emerald-100 bg-emerald-50/40 px-3 py-2"
                                        data-testid="planner-tier-lock-hint"
                                    >
                                        <Lock size={13} weight="duotone" className="text-emerald-700 shrink-0 mt-0.5" />
                                        <div className="text-[11.5px] text-emerald-900 leading-snug">
                                            <span className="font-semibold">Tier locked to aligner count.</span>{" "}
                                            To pick a different tier, update the count in{" "}
                                            <a
                                                href="#sec-wear"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    /* Fire the same "plan-section-open" custom event
                                                       RevisionRequestPanel uses so collapsed
                                                       accordions auto-expand on jump. */
                                                    window.dispatchEvent(
                                                        new CustomEvent("plan-section-open", { detail: { sectionId: "sec-wear" } })
                                                    );
                                                    const target = document.getElementById("sec-wear");
                                                    if (target) {
                                                        target.setAttribute("open", "");
                                                        target.scrollIntoView({ behavior: "smooth", block: "start" });
                                                    }
                                                }}
                                                className="font-semibold text-emerald-800 underline underline-offset-2 hover:text-emerald-900"
                                                data-testid="planner-tier-lock-jump"
                                            >
                                                Section 06 — Aligners &amp; Wear Schedule
                                            </a>.
                                        </div>
                                    </div>
                                )}
                                {/* Out-of-range warning — surfaces when an aligner count is
                                    authored but no active tier covers it. Keeps the choice
                                    blank so the planner can decide manually. */}
                                <TierMatchWarning plan={c.plan} tiers={tiersList} selected={selectedTierId} />
                            </div>
                        )}

                        {/* Notes textarea */}
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">
                                    Notes for the doctor
                                </Label>
                                <span className="text-[10px] text-muted-foreground">Optional</span>
                            </div>
                            <Textarea
                                rows={3}
                                value={reviewNote}
                                onChange={(e) => setReviewNote(e.target.value)}
                                placeholder="Walk the doctor through the setup: extractions, IPR, attachments, expected stages."
                                data-testid="planner-plan-review-note"
                                className="bg-white border-emerald-200 focus-visible:ring-emerald-300 focus-visible:border-emerald-300 text-sm leading-relaxed resize-none rounded-xl shadow-sm"
                            />
                        </div>

                        {/* iter98.99cy — Submit gate RESTORED. The previous
                            "all gates removed" iteration (iter98.99cf)
                            let planners ship cases with no tier picked,
                            which then stalled in the doctor's queue
                            forever. Now: CoE cases require a tier (the
                            tier may have auto-matched OR been picked
                            manually for out-of-range counts). Self-Plan
                            cases already lock the tier at submission so
                            they're naturally green.
                            Backend mirrors this gate with a 400 reject
                            as defence-in-depth. */}
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <p className={`text-[11px] leading-snug flex-1 min-w-[220px] ${tierMissingForSubmit ? "text-rose-700" : "text-muted-foreground"}`}>
                                {tierMissingForSubmit ? (
                                    <>
                                        <span className="font-semibold">Pick a treatment tier above before submitting.</span>{" "}
                                        Update the aligner count in Section 06 or pick a tier manually.
                                    </>
                                ) : (
                                    <>Plan locks on submit · doctor is notified via WhatsApp &amp; email.</>
                                )}
                            </p>
                            <Button
                                onClick={onSend}
                                disabled={busy || tierMissingForSubmit}
                                title={tierMissingForSubmit ? "Pick a treatment tier above to enable submit" : undefined}
                                className="group bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-md shadow-emerald-600/20 hover:shadow-emerald-600/35 transition-all rounded-full h-11 px-5 font-display font-semibold text-sm tracking-wide disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                                data-testid="planner-plan-send-review"
                            >
                                <PaperPlaneRight size={15} weight="fill" className="mr-1.5 group-hover:translate-x-0.5 transition-transform" />
                                Submit to Doctor
                            </Button>
                        </div>
                    </div>
                </details>
                    )}
                />

                {/* iter98.89 — Sticky revision-resubmit footer. Only
                    visible when there's an open revision_request on this
                    case. Mirrors the doctor's "Pending Approval" pill on
                    the other side of the loop. */}
                {openRevision && (
                    <RevisionResubmitFooter
                        revision={openRevision}
                        rows={revisionRows}
                        decisions={revisionDecisions}
                        addressedCount={revisionAddressedCount}
                        allAddressed={revisionAllAddressed}
                        replyNote={revisionReplyNote}
                        setReplyNote={setRevisionReplyNote}
                        submitting={revisionSubmitting}
                        onResubmit={onRevisionResubmit}
                    />
                )}
        </div>
    );
}


/* =====================================================================
 * iter98.89 — Sticky resubmit footer for the Author the plan tab.
 *
 * Renders ONLY when there is an open revision_request on the case. It
 * mirrors the doctor's "Pending Approval" pill on the other side of
 * the loop — same lifecycle, opposite role:
 *
 *   - Counter row: "{addressed} of {total} addressed"
 *   - Reply note textarea (typed feedback that travels back to the
 *     doctor as `actioned_note`).
 *   - Resubmit button — disabled until every banner row has either an
 *     "accepted" or "denied" decision attached.
 *
 * Decisions live in the parent (PlannerCaseDetail) so the inline
 * banners and this footer share state via props.
 * ===================================================================== */
function RevisionResubmitFooter({
    revision, rows, decisions, addressedCount, allAddressed,
    replyNote, setReplyNote, submitting, onResubmit,
}) {
    const total = rows.length;
    const remaining = Math.max(total - addressedCount, 0);
    const accepted = rows.filter((r) => decisions[r.key]?.kind === "accepted").length;
    const denied = rows.filter((r) => decisions[r.key]?.kind === "denied").length;
    return (
        <div
            className="rounded-3xl border-2 border-rose-200 bg-rose-50/60 overflow-hidden"
            data-testid="planner-revision-resubmit-footer"
        >
            <div className="px-6 py-4 border-b border-rose-200/70 bg-rose-100/40">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-rose-700">
                            Doctor&apos;s revision · Iteration {revision?.iteration || 1}
                        </div>
                        <h3 className="font-display text-base font-bold text-rose-900 mt-0.5">
                            {allAddressed
                                ? "All requests addressed — ready to resubmit"
                                : `${remaining} request${remaining === 1 ? "" : "s"} still need a decision`}
                        </h3>
                        <p className="text-xs text-rose-900/70 mt-0.5">
                            <span data-testid="revision-progress-counter">
                                {addressedCount} of {total} addressed
                            </span>
                            {(accepted > 0 || denied > 0) && (
                                <span className="ml-2">
                                    · <span className="text-emerald-700 font-semibold">{accepted} accepted</span>
                                    {denied > 0 && (
                                        <span> · <span className="text-amber-700 font-semibold">{denied} denied</span></span>
                                    )}
                                </span>
                            )}
                        </p>
                    </div>
                    {allAddressed && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.14em] text-emerald-800">
                            <CheckCircle size={13} weight="fill" /> Ready
                        </span>
                    )}
                </div>
            </div>
            <div className="px-6 py-5 space-y-3">
                <div>
                    <Label className="text-[10px] font-bold uppercase tracking-[0.14em] text-rose-800 mb-1.5 block">
                        Reply to the doctor (optional)
                    </Label>
                    <Textarea
                        value={replyNote}
                        onChange={(e) => setReplyNote(e.target.value)}
                        rows={3}
                        placeholder="e.g. Widened the upper arch as requested. Couldn't reduce the IPR on 13-14 below 0.3mm without compromising the rotation — flagged for your sign-off."
                        className="text-sm bg-white border-rose-200"
                        data-testid="planner-reply-note"
                    />
                </div>
                <div className="flex items-center justify-end">
                    <Button
                        onClick={onResubmit}
                        disabled={!allAddressed || submitting}
                        className="bg-rose-700 hover:bg-rose-800 text-white"
                        data-testid="planner-resubmit-revision-btn"
                    >
                        <PaperPlaneRight size={14} className="mr-1.5" weight="fill" />
                        {submitting ? "Sending…" : "Send revised plan to doctor"}
                    </Button>
                </div>
            </div>
        </div>
    );
}


/* iter98.89 — Slim doorway banner shown on the Doctor's Submission
 * tab whenever an open revision_request exists. Replaces the verbose
 * tick-every-row panel for OPEN revisions — per-row decisions now
 * happen INLINE inside the Author the plan tab. This card simply
 * states the context (count, iteration, doctor's summary) and gives
 * the planner a single primary CTA to enter the editor.
 */
function RevisionInlineSummary({
    revision, totalRows, addressedCount, onOpenPlanForRevision,
}) {
    const remaining = Math.max(totalRows - (addressedCount || 0), 0);
    return (
        <div
            className="rounded-2xl border-2 border-rose-200 bg-rose-50/40 overflow-hidden"
            data-testid="planner-revision-summary-banner"
        >
            <div className="px-5 py-4 flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-full bg-rose-200 grid place-items-center shrink-0 mt-0.5">
                        <ClockClockwise size={18} weight="fill" className="text-rose-700" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-rose-700">
                            Doctor&apos;s revision · Iteration {revision.iteration || 1}
                        </div>
                        <h3 className="font-display text-base font-bold text-rose-900 truncate">
                            {revision.requested_by_name || "Doctor"} requested {totalRows} change{totalRows !== 1 ? "s" : ""}
                        </h3>
                        {revision.summary && (
                            <p className="text-xs text-rose-900/85 mt-1 max-w-2xl leading-relaxed line-clamp-3">
                                {revision.summary}
                            </p>
                        )}
                        <div className="text-xs text-rose-900/70 mt-1.5">
                            {remaining > 0
                                ? <>Address {remaining} request{remaining === 1 ? "" : "s"} inline in <span className="font-semibold">Author the plan</span>.</>
                                : <>All {totalRows} request{totalRows === 1 ? "" : "s"} addressed. Resubmit from the plan tab.</>
                            }
                        </div>
                    </div>
                </div>
                <Button
                    onClick={onOpenPlanForRevision}
                    className="bg-rose-700 hover:bg-rose-800 text-white shrink-0"
                    data-testid="planner-open-plan-for-revision-btn"
                >
                    <PencilSimple size={14} className="mr-1.5" weight="duotone" />
                    Open in Author the plan
                </Button>
            </div>
        </div>
    );
}


/* =====================================================================
 * Tab 4 — Private planner notes (chronological journal, 2026-02-27)
 * ===================================================================== */
function PlannerNotesTab({ caseId }) {
    return <CaseNotesJournal caseId={caseId} role="planner" />;
}


/* =====================================================================
 * Local helpers
 * ===================================================================== */
/* iter97.9 — Comprehensive Clinical Record card. Surfaces the entire
   Wave A/B/D clinical intake (occlusion, scan, bite metrics, crowding,
   perio, medical history) the doctor captures during case submission.
   Previously only 5 fields were shown — the planner was missing ~30
   fields of diagnostic context. Subsections collapse when empty so cases
   with sparse intake stay visually clean. */


function ClinicalRecordCard({ c }) {
    const cl = c?.clinical || {};
    const fmtMm = (v) => (v === null || v === undefined || v === "" ? null : `${v} mm`);
    const fmtBool = (v) => (v === true ? "Yes" : v === false ? "No" : null);
    const fmtDate = (iso) => {
        if (!iso) return null;
        try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
        catch { return iso; }
    };

    // Diagnosis subsection (top-level always shown — these are the orientation
    // fields the planner sees first when opening the case).
    const diagnosis = [
        { k: "Case type", v: cl.case_type },
        { k: "Arch", v: cl.arch || c.arches },
        { k: "Malocclusion", v: cl.malocclusion_class },
        { k: "Skeletal class", v: cl.skeletal_class },
        { k: "Angle right", v: cl.angle_class_right },
        { k: "Angle left", v: cl.angle_class_left },
        { k: "Extraction plan", v: cl.extraction_plan },
        { k: "Extraction teeth", v: cl.extraction_teeth },
        { k: "Missing teeth", v: cl.missing_teeth },
    ];
    const occlusion = [
        { k: "Buccal right", v: cl.buccal_occlusion_right },
        { k: "Buccal left", v: cl.buccal_occlusion_left },
        { k: "Canine right", v: cl.canine_class_right },
        { k: "Canine left", v: cl.canine_class_left },
        { k: "Anterior guidance", v: cl.anterior_guidance },
        { k: "Vertical dim.", v: cl.vertical_dimension },
        { k: "Midline", v: cl.midline_relationship },
    ];
    const biteMetrics = [
        { k: "Overbite", v: fmtMm(cl.overbite_mm) },
        { k: "Overjet", v: fmtMm(cl.overjet_mm) },
        { k: "Upper crowding", v: fmtMm(cl.upper_arch_crowding_mm) },
        { k: "Lower crowding", v: fmtMm(cl.lower_arch_crowding_mm) },
        { k: "Crowding distribution", v: cl.crowding_distribution },
    ];
    const scan = [
        { k: "Device", v: cl.scan_device },
        { k: "Model", v: cl.scan_model },
        { k: "Scan date", v: fmtDate(cl.scan_date) },
        { k: "Quality confirmed", v: fmtBool(cl.scan_quality_confirmed) },
        { k: "CBCT available", v: fmtBool(cl.cbct_available) },
    ];
    const periodontal = [
        { k: "Status", v: cl.periodontal_health },
        { k: "Plaque", v: cl.plaque_index },
        { k: "Compliance", v: cl.perio_compliance },
        { k: "Perio notes", v: cl.perio_status, colspan: true },
    ];
    const medical = [
        { k: "Medical history", v: cl.medical_history, colspan: true },
        { k: "Previous ortho", v: cl.previous_ortho },
        { k: "Notes on prior", v: cl.previous_ortho_notes },
    ];

    const sections = [
        { id: "diagnosis", label: "Diagnosis", rows: diagnosis },
        { id: "occlusion", label: "Occlusion", rows: occlusion },
        { id: "bite", label: "Bite metrics & crowding", rows: biteMetrics },
        { id: "scan", label: "Scan & imaging", rows: scan },
        { id: "perio", label: "Periodontal", rows: periodontal },
        { id: "medical", label: "Medical history", rows: medical },
    ];

    return (
        <div className="bg-card border border-border rounded-2xl p-5" data-testid="planner-clinical-record-card">
            <div className="flex items-center justify-between mb-4">
                <div className="overline text-violet-700 flex items-center gap-1.5">
                    <Stethoscope size={12} weight="duotone" className="text-violet-600" />
                    Clinical record
                </div>
            </div>

            {/* Chief complaint banner — always pinned at top because it's
                the doctor's narrative for "why this case". Hide if empty. */}
            {cl.chief_complaint && (
                <div className="rounded-xl bg-amber-50/70 border border-amber-200 px-3.5 py-2.5 mb-4" data-testid="planner-chief-complaint">
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-amber-700 mb-1">Chief complaint</div>
                    <div className="text-sm text-foreground/90 leading-snug">{cl.chief_complaint}</div>
                </div>
            )}

            <div className="space-y-4">
                {sections.map((s) => {
                    const populated = s.rows.filter((r) => r.v !== null && r.v !== undefined && r.v !== "");
                    if (populated.length === 0) return null;
                    return (
                        <div key={s.id} data-testid={`planner-clinical-section-${s.id}`}>
                            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-2">
                                {s.label}
                            </div>
                            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                                {populated.map((r) => (
                                    <KV key={r.k} k={r.k} v={r.v} colspan={r.colspan} />
                                ))}
                            </div>
                        </div>
                    );
                })}

                {/* Special notes — free-text, anything the doctor wanted to
                    flag that didn't fit elsewhere. Render last so it gets
                    the planner's attention right before they start authoring. */}
                {cl.special_notes && (
                    <div className="pt-3 border-t border-border" data-testid="planner-clinical-special-notes">
                        <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-1.5">Special notes</div>
                        <div className="text-sm leading-snug whitespace-pre-wrap text-foreground/85">{cl.special_notes}</div>
                    </div>
                )}
            </div>
        </div>
    );
}


/* Card — iter98.99m → iter98.99au
 *
 * Section card on the planner's case detail page. The `accent` prop is
 * preserved so callers don't break and the title color still ties to
 * the matching doctor-side wizard step (violet=Clinical · amber=Scans
 * · emerald=Treatment · rose=Lab). The colored 4px left-bar treatment
 * was dropped on 2026-02-19 per user direction — felt busy across the
 * planner workspace.
 */
function Card({ title, children, accent = null, icon: IconComp = null, testId = null }) {
    const palette = {
        primary: { title: "text-primary",      icon: "text-primary"      },
        violet:  { title: "text-violet-700",   icon: "text-violet-600"   },
        amber:   { title: "text-amber-800",    icon: "text-amber-700"    },
        emerald: { title: "text-emerald-700",  icon: "text-emerald-600"  },
        rose:    { title: "text-rose-700",     icon: "text-rose-600"     },
    };
    const p = accent ? palette[accent] : null;
    return (
        <div
            className="bg-card border border-border rounded-2xl p-5"
            data-testid={testId || undefined}
        >
            <div className={`overline mb-3 flex items-center gap-1.5 ${p ? p.title : "text-muted-foreground"}`}>
                {IconComp && <IconComp size={12} weight="duotone" className={p?.icon || ""} />}
                {title}
            </div>
            {children}
        </div>
    );
}


/* iter97.45 — HoldAndQueryDialog: header-level button + Dialog the planner
   uses mid-plan to formally PAUSE the case and ask the doctor for missing
   info. Replaces the duplicate "Spotted something missing?" card that used
   to sit on the submission tab post-acceptance. The button is rendered by
   PlannerCaseDetail only when status === in_planning. */
function HoldAndQueryDialog({ note, setNote, onSubmit, busy }) {
    const [open, setOpen] = useState(false);
    const canSubmit = note.trim().length > 0 && !busy;
    const submit = async () => {
        await onSubmit();
        // requestMoreInfo navigates away on success; this close is a safety
        // net in case the navigation is deferred or fails silently.
        setOpen(false);
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button
                    type="button"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-orange-200 bg-white text-xs font-semibold text-orange-700 hover:bg-orange-50 hover:border-orange-300 transition-colors shadow-sm"
                    data-testid="planner-hold-query-trigger"
                >
                    <HandPalm size={13} weight="bold" /> Hold &amp; query doctor
                </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg" data-testid="planner-hold-query-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-lg flex items-center gap-2">
                        <HandPalm size={18} weight="duotone" className="text-orange-600" />
                        Hold case &amp; ask the doctor
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <p className="text-xs text-muted-foreground leading-relaxed">
                        Pauses the case and routes your query to the doctor. They&apos;ll see it in their <span className="font-semibold">Awaiting your input</span> bucket; the case resumes the moment they respond.
                    </p>
                    <div>
                        <Label className="text-xs font-semibold">Your question for the doctor</Label>
                        <Textarea
                            rows={5}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="e.g. The upper-arch STL looks rotated. Can you re-scan and re-upload? Or — Chief complaint mentions deep bite but I don't see overbite measurement."
                            className="resize-none text-sm mt-1.5"
                            data-testid="planner-hold-query-note"
                            disabled={busy}
                        />
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-1">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setOpen(false)}
                            disabled={busy}
                            className="rounded-full"
                            data-testid="planner-hold-query-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={submit}
                            disabled={!canSubmit}
                            className="rounded-full bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="planner-hold-query-submit"
                        >
                            <ChatCircleDots size={14} className="mr-1.5" />
                            {busy ? "Pausing…" : "Pause & send query"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Stat({ label, children }) {
    return (
        <div className="min-w-0">
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-1.5">
                {label}
            </div>
            {/* `overflow-hidden` + `min-w-0` clip the cell at its grid
               boundary. Inline-flex children (the status pill) would
               otherwise grow to fit content like "Awaiting Doctor
               Approval" and bleed into the adjacent "Case code" cell —
               drawing the case code on top of the pill (iter98.fix-overlap). */}
            <div className="min-w-0 overflow-hidden">{children}</div>
        </div>
    );
}


// iter98.99at — Tiny inline pill (formerly `DoctorPrioritySetBadge`),
// designed for the lean tier+submit footer. Doctor-set manufacturing
// speed is read-only context, not a major affordance — so a one-line
// pill is the right weight. Tooltip on hover exposes the full TAT + fee
// breakdown for planners who want it.
function DoctorPriorityPill({ pricing }) {
    const code = pricing?.priority_code || "standard";
    const label = pricing?.priority_label || "Standard TAT";
    const fee = Number(pricing?.priority_fee || 0);
    const days = pricing?.priority_tat_days;
    const isRush = code !== "standard";
    const detail = [
        label,
        days ? `${days}d TAT` : null,
        fee > 0 ? `+₹${Math.round(fee).toLocaleString("en-IN")}` : null,
    ].filter(Boolean).join(" · ");
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold ${
                isRush
                    ? "bg-amber-50 border-amber-200 text-amber-800"
                    : "bg-secondary/60 border-border text-foreground/80"
            }`}
            title={`Manufacturing speed set by doctor · ${detail}`}
            data-testid="planner-doctor-priority-pill"
        >
            <Hourglass size={11} weight="fill" className={isRush ? "text-amber-700" : "text-muted-foreground"} />
            <span className="uppercase tracking-[0.12em]">Doctor speed</span>
            <span className="text-foreground/40">·</span>
            <span className="normal-case tracking-normal">{label}</span>
            {days ? <span className="text-foreground/60 normal-case tracking-normal">· {days}d</span> : null}
            {fee > 0 ? <span className="text-foreground/60 normal-case tracking-normal">· +₹{Math.round(fee).toLocaleString("en-IN")}</span> : null}
        </span>
    );
}

function KV({ k, v, colspan }) {
    return (
        <div className={`text-sm flex gap-2 items-baseline ${colspan ? "sm:col-span-2" : ""}`}>
            <div className="text-muted-foreground text-xs min-w-[110px]">{k}</div>
            <div className="flex-1">{v || <span className="text-muted-foreground/60">—</span>}</div>
        </div>
    );
}

function FileLink({ f, Icon, kindBadge, isNew }) {
    return (
        <a
            href={`${API}/files/${f.id}?auth=${localStorage.getItem("ca_token")}`}
            target="_blank" rel="noopener noreferrer"
            className={`text-sm flex items-center gap-2 hover:text-primary rounded-md px-1 -mx-1 ${
                isNew ? "bg-emerald-50/60" : ""
            }`}
            data-testid={isNew ? `planner-filelink-replaced-${f.id}` : undefined}
        >
            <Icon size={14} /> {f.name}
            {kindBadge && <span className="text-[10px] uppercase text-muted-foreground">[{f.kind}]</span>}
            {isNew && (
                <span
                    className="ml-auto inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded"
                    title="Replaced after your info-request"
                >
                    <ArrowsClockwise size={8} weight="bold" /> Replaced
                </span>
            )}
        </a>
    );
}


/* Pinned-at-top banner in the Decision Panel that surfaces an unanswered
   doctor message on this case. Clicking jumps into the Lab Chat thread.
   Renders nothing if there's no `unread_from_doctor` sidecar — the parent
   already guards against this, but keep the safety. */
function UnreadDoctorBanner({ u, caseId }) {
    if (!u) return null;
    // Human-friendly "x min/h/d ago" relative time so the planner can
    // gauge urgency without parsing an ISO string.
    const ago = (() => {
        if (!u.created_at) return "";
        const ms = Date.now() - new Date(u.created_at).getTime();
        if (Number.isNaN(ms) || ms < 0) return "";
        const mins = Math.round(ms / 60_000);
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.round(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.round(hrs / 24);
        return `${days}d ago`;
    })();
    const isClinicStaff = u.author_role === "clinic_staff";
    const author = isClinicStaff
        ? `${u.author_name} · Clinic`
        : `Dr. ${(u.author_name || "Doctor").replace(/^Dr\.\s*/i, "")}`;
    return (
        <Link
            to={`/coe/planner/provider-chat?case=${caseId}`}
            className="block mb-4 rounded-xl bg-cyan-50 border border-cyan-200 hover:border-cyan-400 hover:bg-cyan-50/80 transition-colors p-3"
            data-testid="planner-decision-unread-doctor"
        >
            <div className="flex items-start gap-2.5">
                <div className="w-8 h-8 rounded-full bg-cyan-100 grid place-items-center shrink-0">
                    <ChatCircleDots size={15} weight="fill" className="text-cyan-700" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[11px] font-semibold text-cyan-900">{author}</span>
                        {ago && (
                            <span className="text-[10px] text-cyan-800/70">· {ago}</span>
                        )}
                        <span className="ml-auto text-[10px] font-bold tracking-[0.16em] uppercase text-cyan-700 shrink-0">
                            Unread
                        </span>
                    </div>
                    <p className="text-xs text-cyan-900/90 mt-1 line-clamp-2 leading-snug">
                        {u.preview || "Open the thread to read the full message."}
                    </p>
                    <p className="text-[10px] text-cyan-700 font-semibold mt-1.5">
                        Open in Lab Chat →
                    </p>
                </div>
            </div>
        </Link>
    );
}


/* The standalone RequestInfoDialog was retired in 2026-02-24: the ADR flow
   now lives inline in the Submission tab's DecisionPanel, so the planner
   never has to leave the case context. Keeping this comment here as a
   pointer for anyone digging through `git log`. */

/* Activity timeline is now sourced from the shared CaseTimelineTab
   component (also used by the doctor side) — see imports above. */

